import { useState, useEffect, useCallback } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

// ---- Typy ----
interface Meter {
  id?: number
  key?: string
  label: string
  unit: string
  kind: string // water | electricity | other
  sort_order: number
  is_active: boolean
  meter_serial?: string | null
}
interface Reading {
  meter_id: number
  meter_key?: string
  meter_label?: string
  unit?: string
  kind?: string
  value: number | null
  delta: number | null
}
interface Photo { id: number; url: string; caption?: string | null }
interface Entry {
  id: number
  visited_at: string | null
  note: string | null
  author_name: string | null
  created_at: string | null
  edited_by_name: string | null
  edited_at: string | null
  readings: Reading[]
  photos: Photo[]
}
interface Config {
  enabled: boolean
  infrastructure_type?: string | null
  meters: Meter[]
  template_available: boolean
  template_meters: { key: string; label: string; unit: string; kind: string }[]
}

interface Props {
  objectId: number
  objectName?: string
  token: string
  userRole?: string
}

const KIND_LABELS: Record<string, string> = { water: '💧 Woda', electricity: '⚡ Prąd', other: '🔧 Inne' }

function slugify(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/ł/g, 'l') // ł
    .normalize('NFD').replace(/[̀-ͯ]/g, '') // usuń diakrytyki
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'licznik'
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString('pl-PL', { dateStyle: 'medium', timeStyle: 'short' })
}

function photoSrc(url: string): string {
  return url.startsWith('http') ? url : '/' + url.replace(/^\/+/, '')
}

export default function LogbookTab({ objectId, objectName, token, userRole }: Props) {
  const { showSuccess, showError } = useNotificationContext()
  const canConfigure = ['KIEROWNIK', 'ADMIN', 'MASTER_ADMIN'].includes(userRole || '')
  const canEditEntries = userRole === 'MASTER_ADMIN'

  const [config, setConfig] = useState<Config | null>(null)
  const [entries, setEntries] = useState<Entry[]>([])
  const [loading, setLoading] = useState(true)
  const [showConfig, setShowConfig] = useState(false)
  const [showAdd, setShowAdd] = useState(false)

  const authHeaders = { Authorization: `Bearer ${token}` }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const cRes = await fetch(`/api/infrastructure/${objectId}/logbook/config`, { headers: authHeaders })
      if (cRes.ok) {
        const c = await cRes.json()
        setConfig(c)
        if (c.enabled) {
          const eRes = await fetch(`/api/infrastructure/${objectId}/logbook/entries`, { headers: authHeaders })
          if (eRes.ok) setEntries(await eRes.json())
        }
      }
    } catch {
      showError('Błąd', 'Nie udało się pobrać dziennika')
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objectId, token])

  useEffect(() => { load() }, [load])

  if (loading) return <div className="py-8 text-center text-gray-500">Ładowanie dziennika…</div>
  if (!config) return <div className="py-8 text-center text-gray-500">Brak danych dziennika.</div>

  // --- Dziennik wyłączony ---
  if (!config.enabled && !showConfig) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-600 mb-4">📔 Dziennik nie jest włączony dla tego obiektu.</p>
        {canConfigure ? (
          <button onClick={() => setShowConfig(true)}
            className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
            Włącz i skonfiguruj dziennik
          </button>
        ) : (
          <p className="text-sm text-gray-400">Poproś kierownika o włączenie dziennika dla tego obiektu.</p>
        )}
      </div>
    )
  }

  if (showConfig) {
    return <ConfigPanel objectId={objectId} token={token} config={config}
      onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); load() }} />
  }

  const activeMeters = config.meters.filter(m => m.is_active)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-700">📔 Dziennik obiektu {objectName ? `— ${objectName}` : ''}</h3>
        <div className="flex gap-2">
          {canConfigure && (
            <button onClick={() => setShowConfig(true)}
              className="min-h-[40px] px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">
              ⚙️ Liczniki
            </button>
          )}
          <button onClick={() => setShowAdd(true)}
            className="min-h-[40px] px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg">
            ➕ Dodaj wpis
          </button>
        </div>
      </div>

      {showAdd && (
        <AddEntryForm objectId={objectId} token={token} meters={activeMeters}
          onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load() }} />
      )}

      {entries.length === 0 ? (
        <p className="py-6 text-center text-gray-400 text-sm">Brak wpisów. Dodaj pierwszy wpis z wizyty.</p>
      ) : (
        <div className="space-y-3">
          {entries.map(e => (
            <EntryCard key={e.id} entry={e} canEdit={canEditEntries} token={token}
              meters={config.meters} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  )
}

// ===================== Karta wpisu =====================
function EntryCard({ entry, canEdit, token, meters, onChanged }:
  { entry: Entry; canEdit: boolean; token: string; meters: Meter[]; onChanged: () => void }) {
  const { showSuccess, showError } = useNotificationContext()
  const [editing, setEditing] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)

  const del = async () => {
    try {
      const r = await fetch(`/api/infrastructure/logbook/entries/${entry.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      })
      if (r.ok) { showSuccess('Usunięto wpis', ''); onChanged() }
      else showError('Błąd', (await r.json()).detail || 'Nie udało się usunąć')
    } catch { showError('Błąd', 'Problem z połączeniem') }
  }

  if (editing) {
    return <EditEntryForm entry={entry} token={token} meters={meters}
      onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged() }} />
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-gray-900">🗓 {fmtDateTime(entry.visited_at)}</div>
          <div className="text-xs text-gray-500">{entry.author_name || 'Nieznany'}</div>
        </div>
        {canEdit && (
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded">✏️ Edytuj</button>
            {confirmDel ? (
              <>
                <button onClick={del} className="text-xs px-2 py-1 text-white bg-red-600 rounded">Usuń</button>
                <button onClick={() => setConfirmDel(false)} className="text-xs px-2 py-1 text-gray-600 bg-gray-100 rounded">Nie</button>
              </>
            ) : (
              <button onClick={() => setConfirmDel(true)} className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded">🗑</button>
            )}
          </div>
        )}
      </div>

      {entry.note && <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{entry.note}</p>}

      {entry.readings.length > 0 && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {entry.readings.map(r => (
            <div key={r.meter_id} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
              <span className="text-gray-600">{r.meter_label}</span>
              <span className="text-gray-900 font-medium">
                {r.value != null ? r.value.toLocaleString('pl-PL') : '—'} {r.unit}
                {r.delta != null && (
                  <span className={`ml-2 text-xs ${r.delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    (Δ {r.delta >= 0 ? '+' : ''}{r.delta.toLocaleString('pl-PL')})
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {entry.photos.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {entry.photos.map(p => (
            <a key={p.id} href={photoSrc(p.url)} target="_blank" rel="noreferrer">
              <img src={photoSrc(p.url)} alt={p.caption || 'zdjęcie'}
                className="w-16 h-16 object-cover rounded border border-gray-200" />
            </a>
          ))}
        </div>
      )}

      {entry.edited_by_name && (
        <p className="mt-2 text-xs text-amber-600">✎ Edytowano: {entry.edited_by_name} • {fmtDateTime(entry.edited_at)}</p>
      )}
    </div>
  )
}

// ===================== Formularz dodawania wpisu =====================
function nowLocalInput(): string {
  const d = new Date()
  const off = d.getTimezoneOffset()
  const local = new Date(d.getTime() - off * 60000)
  return local.toISOString().slice(0, 16)
}

function AddEntryForm({ objectId, token, meters, onClose, onSaved }:
  { objectId: number; token: string; meters: Meter[]; onClose: () => void; onSaved: () => void }) {
  const { showSuccess, showError } = useNotificationContext()
  const [visitedAt, setVisitedAt] = useState(nowLocalInput())
  const [note, setNote] = useState('')
  const [values, setValues] = useState<Record<number, string>>({})
  const [photoPaths, setPhotoPaths] = useState<{ path: string; url: string }[]>([])
  const [uploading, setUploading] = useState(false)
  const [saving, setSaving] = useState(false)

  const uploadPhotos = async (files: FileList) => {
    setUploading(true)
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData()
        fd.append('file', f)
        const r = await fetch('/api/infrastructure/logbook/upload-photo', {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        })
        if (r.ok) { const d = await r.json(); setPhotoPaths(prev => [...prev, { path: d.file_path, url: d.url }]) }
        else showError('Błąd', 'Nie udało się wgrać zdjęcia')
      }
    } finally { setUploading(false) }
  }

  const save = async () => {
    const readings = meters
      .filter(m => m.id != null && values[m.id!] !== undefined && values[m.id!] !== '')
      .map(m => ({ meter_id: m.id!, value: parseFloat(values[m.id!].replace(',', '.')) }))
      .filter(r => !isNaN(r.value))
    if (!note.trim() && readings.length === 0 && photoPaths.length === 0) {
      showError('Pusty wpis', 'Dodaj notatkę, odczyt lub zdjęcie'); return
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/infrastructure/${objectId}/logbook/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          visited_at: new Date(visitedAt).toISOString(),
          note: note.trim() || null,
          readings,
          photo_paths: photoPaths.map(p => p.path),
        }),
      })
      if (r.ok) { showSuccess('Zapisano wpis', ''); onSaved() }
      else showError('Błąd', (await r.json()).detail || 'Nie udało się zapisać')
    } catch { showError('Błąd', 'Problem z połączeniem') } finally { setSaving(false) }
  }

  return (
    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/40 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">➕ Nowy wpis</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Data i czas wizyty</label>
        <input type="datetime-local" value={visitedAt} onChange={e => setVisitedAt(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Notatka z wizyty</label>
        <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
          placeholder="Obserwacje, wykonane czynności…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
      </div>

      {meters.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Odczyty liczników (stan)</label>
          <div className="space-y-1.5">
            {meters.map(m => (
              <div key={m.id} className="flex items-center gap-2">
                <span className="text-sm text-gray-700 w-40 shrink-0">{m.label}</span>
                <input type="text" inputMode="decimal" value={values[m.id!] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [m.id!]: e.target.value }))}
                  placeholder="stan licznika"
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                <span className="text-xs text-gray-500 w-10">{m.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Zdjęcia</label>
        <input type="file" accept="image/*" multiple
          onChange={e => e.target.files && uploadPhotos(e.target.files)}
          className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700" />
        {uploading && <p className="text-xs text-gray-500 mt-1">Wgrywanie…</p>}
        {photoPaths.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {photoPaths.map((p, i) => (
              <div key={i} className="relative">
                <img src={photoSrc(p.url)} className="w-14 h-14 object-cover rounded border border-gray-200" />
                <button onClick={() => setPhotoPaths(prev => prev.filter((_, j) => j !== i))}
                  className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 text-xs leading-none">×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="min-h-[40px] px-3 py-1.5 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg">Anuluj</button>
        <button onClick={save} disabled={saving || uploading}
          className="min-h-[40px] px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
          {saving ? 'Zapisywanie…' : 'Zapisz wpis'}
        </button>
      </div>
      <p className="text-xs text-gray-400">Uwaga: po zapisaniu wpisu nie można go edytować (poprawki tylko przez administratora).</p>
    </div>
  )
}

// ===================== Edycja wpisu (MASTER_ADMIN) =====================
function EditEntryForm({ entry, token, meters, onClose, onSaved }:
  { entry: Entry; token: string; meters: Meter[]; onClose: () => void; onSaved: () => void }) {
  const { showSuccess, showError } = useNotificationContext()
  const [note, setNote] = useState(entry.note || '')
  const initVals: Record<number, string> = {}
  entry.readings.forEach(r => { if (r.value != null) initVals[r.meter_id] = String(r.value) })
  const [values, setValues] = useState<Record<number, string>>(initVals)
  const [saving, setSaving] = useState(false)
  const activeMeters = meters.filter(m => m.is_active || initVals[m.id!] !== undefined)

  const save = async () => {
    const readings = activeMeters
      .filter(m => m.id != null && values[m.id!] !== undefined && values[m.id!] !== '')
      .map(m => ({ meter_id: m.id!, value: parseFloat(String(values[m.id!]).replace(',', '.')) }))
      .filter(r => !isNaN(r.value))
    setSaving(true)
    try {
      const r = await fetch(`/api/infrastructure/logbook/entries/${entry.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ note: note.trim() || null, readings }),
      })
      if (r.ok) { showSuccess('Zaktualizowano', ''); onSaved() }
      else showError('Błąd', (await r.json()).detail || 'Nie udało się zapisać')
    } catch { showError('Błąd', 'Problem z połączeniem') } finally { setSaving(false) }
  }

  return (
    <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50/40 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">✏️ Edycja wpisu (administrator)</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
      {activeMeters.length > 0 && (
        <div className="space-y-1.5">
          {activeMeters.map(m => (
            <div key={m.id} className="flex items-center gap-2">
              <span className="text-sm text-gray-700 w-40 shrink-0">{m.label}</span>
              <input type="text" inputMode="decimal" value={values[m.id!] ?? ''}
                onChange={e => setValues(v => ({ ...v, [m.id!]: e.target.value }))}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <span className="text-xs text-gray-500 w-10">{m.unit}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="min-h-[40px] px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg">Anuluj</button>
        <button onClick={save} disabled={saving}
          className="min-h-[40px] px-4 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50">
          {saving ? 'Zapisywanie…' : 'Zapisz zmiany'}
        </button>
      </div>
    </div>
  )
}

// ===================== Panel konfiguracji liczników =====================
function ConfigPanel({ objectId, token, config, onClose, onSaved }:
  { objectId: number; token: string; config: Config; onClose: () => void; onSaved: () => void }) {
  const { showSuccess, showError } = useNotificationContext()
  const [enabled, setEnabled] = useState(config.enabled)
  const [meters, setMeters] = useState<Meter[]>(config.meters.map(m => ({ ...m })))
  const [saving, setSaving] = useState(false)

  const addMeter = () => setMeters(prev => [...prev,
    { label: '', unit: 'm³', kind: 'water', sort_order: prev.length, is_active: true }])

  const applyTemplate = () => {
    const existingKeys = new Set(meters.map(m => m.key || slugify(m.label)))
    const toAdd = config.template_meters
      .filter(t => !existingKeys.has(t.key))
      .map((t, i) => ({ key: t.key, label: t.label, unit: t.unit, kind: t.kind, sort_order: meters.length + i, is_active: true }))
    if (toAdd.length === 0) { showError('Szablon', 'Wszystkie liczniki z szablonu już istnieją'); return }
    setMeters(prev => [...prev, ...toAdd])
  }

  const update = (i: number, patch: Partial<Meter>) =>
    setMeters(prev => prev.map((m, j) => j === i ? { ...m, ...patch } : m))
  const remove = (i: number) => setMeters(prev => prev.filter((_, j) => j !== i))

  const save = async () => {
    const payload = {
      enabled,
      meters: meters
        .filter(m => m.label.trim())
        .map((m, i) => ({
          id: m.id, key: m.key || slugify(m.label), label: m.label.trim(),
          unit: m.unit || 'm³', kind: m.kind || 'water', sort_order: i,
          is_active: m.is_active, meter_serial: m.meter_serial || null,
        })),
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/infrastructure/${objectId}/logbook/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })
      if (r.ok) { showSuccess('Zapisano konfigurację', ''); onSaved() }
      else showError('Błąd', (await r.json()).detail || 'Nie udało się zapisać')
    } catch { showError('Błąd', 'Problem z połączeniem') } finally { setSaving(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">⚙️ Konfiguracja dziennika</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>

      <label className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 cursor-pointer">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
          className="w-5 h-5 text-blue-600 rounded" />
        <span className="text-sm font-medium text-gray-800">Dziennik włączony dla tego obiektu</span>
      </label>

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">Liczniki</span>
        <div className="flex gap-2">
          {config.template_available && (
            <button onClick={applyTemplate} className="text-xs px-2 py-1 text-blue-700 bg-blue-50 hover:bg-blue-100 rounded">
              📋 Zastosuj szablon typu
            </button>
          )}
          <button onClick={addMeter} className="text-xs px-2 py-1 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded">
            ➕ Dodaj licznik
          </button>
        </div>
      </div>

      {meters.length === 0 ? (
        <p className="text-sm text-gray-400">Brak liczników. Dodaj ręcznie lub zastosuj szablon typu obiektu.</p>
      ) : (
        <div className="space-y-2">
          {meters.map((m, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 border border-gray-200 rounded-lg p-2">
              <input value={m.label} onChange={e => update(i, { label: e.target.value })}
                placeholder="Nazwa licznika" className="flex-1 min-w-[140px] border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <input value={m.unit} onChange={e => update(i, { unit: e.target.value })}
                className="w-16 border border-gray-300 rounded px-2 py-1.5 text-sm" />
              <select value={m.kind} onChange={e => update(i, { kind: e.target.value })}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={m.is_active} onChange={e => update(i, { is_active: e.target.checked })} />
                aktywny
              </label>
              <button onClick={() => remove(i)} className="text-red-500 hover:text-red-700 text-sm px-1">🗑</button>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button onClick={onClose} className="min-h-[40px] px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg">Anuluj</button>
        <button onClick={save} disabled={saving}
          className="min-h-[40px] px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
          {saving ? 'Zapisywanie…' : 'Zapisz konfigurację'}
        </button>
      </div>
    </div>
  )
}
